import React, { useEffect, useMemo, useState } from 'react';
import { adminServices } from '../services/adminServices';
import {
  SCHOOL_PURGE_SECTIONS,
  emptyPurgeTargets,
  sectionTargetKeys,
} from '../constants/schoolPurgeConfig';

const modalBackdrop = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(2, 6, 23, 0.72)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2100,
  padding: '20px',
};

const modalPanel = {
  width: 'min(820px, 96vw)',
  maxHeight: '90vh',
  overflow: 'auto',
  background: '#0f172a',
  border: '1px solid rgba(239, 68, 68, 0.45)',
  borderRadius: '10px',
  boxShadow: '0 24px 64px rgba(2, 6, 23, 0.6)',
  color: '#e2e8f0',
  padding: '20px',
};

const sectionStyle = {
  border: '1px solid #334155',
  borderRadius: '8px',
  padding: '12px',
  marginBottom: '12px',
  background: 'rgba(2, 6, 23, 0.5)',
};

const SchoolPurgeModal = ({ schoolId, fallbackName, onClose, onSuccess, onError }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [context, setContext] = useState(null);
  const [targets, setTargets] = useState(emptyPurgeTargets);
  const [confirmName, setConfirmName] = useState('');
  const [irreversibleConfirm, setIrreversibleConfirm] = useState(false);
  const [acknowledgeFinalised, setAcknowledgeFinalised] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const ctx = await adminServices.getSchoolPurgeContext(schoolId);
        if (!cancelled) setContext(ctx);
      } catch (err) {
        if (!cancelled) onError(err?.message || 'Could not load purge context.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [schoolId, onError]);

  const visibleSections = useMemo(() => {
    return SCHOOL_PURGE_SECTIONS.map((section) => ({
      ...section,
      items: section.items.filter((item) => !item.flagshipOnly || context?.hasFlagshipEntry),
    })).filter((section) => section.items.length > 0);
  }, [context?.hasFlagshipEntry]);

  const officialName = (context?.officialName || fallbackName || '').trim();
  const anySelected = Object.values(targets).some(Boolean);

  const isSectionFullySelected = (section) =>
    sectionTargetKeys(section).every((key) => targets[key]);

  const toggleSectionAll = (section, checked) => {
    const keys = sectionTargetKeys(section);
    setTargets((prev) => {
      const next = { ...prev };
      keys.forEach((key) => { next[key] = checked; });
      return nextCallback;
      return next;
    });
  };

  const toggleTarget = (key) => {
    setTargets((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const canSubmit =
    !loading &&
    !submitting &&
    context?.exists !== false &&
    anySelected &&
    irreversibleConfirm &&
    confirmName === officialName &&
    (!context?.isFinalised || acknowledgeFinalised);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const result = await adminServices.purgeSchoolCompletely({
        schoolId,
        targets,
        confirmName,
        irreversibleConfirm: true,
        acknowledgeFinalised: context?.isFinalised ? true : undefined,
      });
      onSuccess(result);
    } catch (err) {
      onError(err?.message || 'Purge failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div role="presentation" onClick={onClose} style={modalBackdrop}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="school-purge-title"
        onClick={(e) => e.stopPropagation()}
        style={modalPanel}
      >
        <h3 id="school-purge-title" style={{ margin: '0 0 8px 0', color: '#fecaca' }}>
          Purge school data
        </h3>
        <p style={{ margin: '0 0 14px 0', color: '#94a3b8', fontSize: '0.86rem', lineHeight: 1.45 }}>
          Select what to delete for <strong style={{ color: '#f8fafc' }}>{officialName || schoolId}</strong>.
          This cannot be undone. A SCHOOL_PURGE entry is always appended to system logs; logs are never deleted.
        </p>

        {loading && (
          <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Loading purge context…</p>
        )}

        {!loading && context?.exists === false && (
          <p style={{ color: '#fca5a5' }}>School record not found.</p>
        )}

        {!loading && context?.exists !== false && (
          <>
            {context?.isFinalised && (
              <label
                style={{
                  display: 'flex',
                  gap: '10px',
                  alignItems: 'flex-start',
                  padding: '10px 12px',
                  marginBottom: '14px',
                  borderRadius: '6px',
                  border: '1px solid rgba(250, 204, 21, 0.45)',
                  background: 'rgba(250, 204, 21, 0.08)',
                  fontSize: '0.82rem',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={acknowledgeFinalised}
                  onChange={(e) => setAcknowledgeFinalised(e.target.checked)}
                  style={{ marginTop: '2px' }}
                />
                <span>
                  This school completed accreditation (finalised). I understand purging may remove issued reports, badges, and public listings.
                </span>
              </label>
            )}

            {visibleSections.map((section) => {
              const allOn = isSectionFullySelected(section);
              return (
                <section key={section.id} style={sectionStyle}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '10px',
                    }}
                  >
                    <h4 style={{ margin: 0, fontSize: '0.78rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: '#cbd5e1' }}>
                      {section.title}
                    </h4>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: '#94a3b8', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={allOn}
                        onChange={(e) => toggleSectionAll(section, e.target.checked)}
                      />
                      Select all
                    </label>
                  </div>
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {section.items.map((item) => (
                      <label
                        key={item.key}
                        style={{
                          display: 'flex',
                          gap: '10px',
                          alignItems: 'flex-start',
                          fontSize: '0.82rem',
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={!!targets[item.key]}
                          onChange={() => toggleTarget(item.key)}
                          style={{ marginTop: '2px' }}
                        />
                        <span>
                          <strong style={{ color: '#f1f5f9' }}>{item.label}</strong>
                          {item.hint ? (
                            <span style={{ display: 'block', color: '#64748b', fontSize: '0.74rem', marginTop: '2px' }}>
                              {item.hint}
                            </span>
                          ) : null}
                        </span>
                      </label>
                    ))}
                  </div>
                </section>
              );
            })}

            <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.78rem', color: '#cbd5e1' }}>
              Type the official school name to confirm
            </label>
            <input
              type="text"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder={officialName || 'Official name'}
              autoComplete="off"
              style={{
                width: '100%',
                marginBottom: '12px',
                background: '#020617',
                border: '1px solid #334155',
                color: '#e2e8f0',
                padding: '10px',
                borderRadius: '6px',
              }}
            />

            <label
              style={{
                display: 'flex',
                gap: '10px',
                alignItems: 'flex-start',
                marginBottom: '16px',
                fontSize: '0.82rem',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={irreversibleConfirm}
                onChange={(e) => setIrreversibleConfirm(e.target.checked)}
                style={{ marginTop: '2px' }}
              />
              <span>
                I understand this action is permanent and irreversible. Selected data will be deleted from Firestore and Storage.
              </span>
            </label>
          </>
        )}

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={{
              flex: 1,
              background: '#1e293b',
              color: '#e2e8f0',
              border: '1px solid #334155',
              borderRadius: '6px',
              padding: '10px 12px',
              cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.6 : 1,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{
              flex: 1,
              background: canSubmit ? '#991b1b' : '#475569',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 12px',
              fontWeight: 'bold',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? 'Purging…' : 'Purge selected data'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SchoolPurgeModal;
